import { FC, useEffect, useState } from 'react';

import { StockSearchBar } from './StockSearchBar';
// import { StockComparisonTabs } from './StockComparisonTabs';
import { TableView } from './views/TableView';
import { ChartView } from './views/ChartView';
import StockService from '../../Api_Module/Api_Services/StockService';
import LoaderHelper from '../../utility/Loading/LoaderHelper';
import { useLocation, useParams } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';

export type Stock = {
  symbol: string;
  name: string;
  metrics: Record<string, any>;
};
export type ComparisonTab = 'table' | 'chart';

type Props = { variant?: 'single' | 'flat' };

const StockComparison: FC<Props> = ({ variant = 'single' }) => {
  const { symbol } = useParams<{ symbol: string }>();
  const location = useLocation();
  const [selectedStocks, setSelectedStocks] = useState<Stock[]>([]);
  const [activeTab] = useState<ComparisonTab>('table');
  const [includeBenchmark, setIncludeBenchmark] = useState(false);
  const [tableMetrics, setTableMetrics] = useState<any>([]);
  const [benchmarkMetrics, setBenchmarkMetrics] = useState<any>([]);
  const [isComparisonCollapsed, setIsComparisonCollapsed] = useState(false);
  const [defaultStock, setDefaultStock] = useState<Stock | null>(null);
  const metricKeys = [
    {
      id: 'ttmPE',
      label: 'TTM P/E'
    },
    {
      id: 'forwardPE',
      label: 'Forward P/E'
    },
    {
      id: 'twoYearForwardPE',
      label: '2Y Forward P/E'
    },
    {
      id: 'ttmEPSGrowth',
      label: 'TTM EPS Growth'
    },
    // {
    //   id: 'projectedNTMEpsGrowth',
    //   label: 'NTM Revenue Growth'
    // },
    {
      id: 'ttmRevenueGrowth',
      label: 'TTM Revenue Growth'
    },
    {
      id: 'nextYearRevenueGrowth',
      label: 'NTM Revenue Growth'
    }, 
    {
      id: 'grossMargin',
      label: 'TTM Gross Margin'
    },
    {
      id: 'netMargin',
      label: 'TTM Net Margin'
    },
    {
      id: 'operatingMargin',
      label: 'TTM Operating Margin'
    }
  ]
  useEffect(() => {
    if (symbol) {
      getMetricsBySymbol(symbol).then(metrics => {
        if (metrics) {
          let stock : Stock = {
            symbol,
            name: symbol || '',
            metrics: metrics
          };
          // setSelectedStocks([{ symbol, name: '', metrics }]);
          setDefaultStock(stock);
          setSelectedStocks(prev => prev.find(s => s.symbol === stock.symbol) ? prev : [...prev, stock]);
          const metricsList = Object.keys(metrics).map(key => ({
            id: key,
            label:  metrics[key]?.label || key.replace(/_/g, ' ').toUpperCase(),
          }));
          setTableMetrics(metricsList);
        } else {
          console.warn('No metrics found for symbol:', symbol);
        }
      });
    }
  }, [symbol]);

  // Parse query param ?symbols=AAPL,MSFT etc and prefill
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const symbolsParam = params.get('symbols');
    if (!symbolsParam) return;
    const list = symbolsParam.split(',').map(s => s.trim().toUpperCase()).filter(Boolean).slice(0, 3);
    if (list.length === 0) return;

    // Avoid re-adding if already loaded
    const toFetch = list.filter(s => !selectedStocks.find(st => st.symbol === s));
    if (toFetch.length === 0) return;

    Promise.all(toFetch.map(async (s) => {
      const metrics = await getMetricsBySymbol(s);
      if (metrics) {
        return { symbol: s, name: s, metrics } as Stock;
      }
      return null;
    })).then(results => {
      const valid = results.filter(Boolean) as Stock[];
      if (valid.length > 0) {
        setSelectedStocks(prev => {
          const merged = [...prev];
          valid.forEach(v => { if (!merged.find(m => m.symbol === v.symbol)) merged.push(v) });
          return merged;
        });
        // Update table metrics based on the first valid set
        const first = valid[0];
        const metricsList = Object.keys(first.metrics).map(key => ({
          id: key,
          label: first.metrics[key]?.label || key.replace(/_/g, ' ').toUpperCase(),
        }));
        setTableMetrics(metricsList);
      }
    });
  }, [location.search]);

  const handleToggleBenchmark = () => {
    setIncludeBenchmark(!includeBenchmark);
    if (!includeBenchmark) {
      getBenchmarkMetrics();
    } else {
      setBenchmarkMetrics([]);
    }
  };
  const getBenchmarkMetrics = async () => {
    try {
      LoaderHelper.loaderStatus(true);
      const result = await StockService.getMetricsBySymbolsSPY();
      LoaderHelper.loaderStatus(false);
      if (result) {
        const spy = result['SPY'];
        const NASDAQ = result['NASDAQ-100'];
        const newSpy: any = {};
        const newNASDAQ: any = {};
        metricKeys.forEach((key: any) => {
          if( spy[key.label] !== undefined && spy[key.label] !== null) {
            newSpy[key.id] = {
              value: (spy[key.label] !== undefined) ? spy[key.label] : "N/A"
            };
          }
          if( NASDAQ[key.label] !== undefined && NASDAQ[key.label] !== null) {
            newNASDAQ[key.id] = {
              value: (NASDAQ[key.label] !== undefined) ? NASDAQ[key.label] : "N/A"
            };
          }
        });
        setBenchmarkMetrics([{
          symbol: 'SPY',
          name: 'S&P 500',
          metrics: newSpy,
        },{
          symbol: 'NASDAQ-100',
          name: 'NASDAQ-100',
          metrics: newNASDAQ,
        }]);
      } else {
        console.warn('No benchmark metrics found');
      }
    }
    catch (error) {
      LoaderHelper.loaderStatus(false);
      console.error('Error fetching benchmark metrics:', error);
    }
  }
  const getMetricsBySymbol = async (symbol: string) => {
    try {
      LoaderHelper.loaderStatus(true);
      const result = await StockService.getMetricsBySymbol(symbol);
      LoaderHelper.loaderStatus(false);
      if (!result || Object.keys(result).length === 0) {
        console.warn('No metrics found for', symbol);
        return null;
      }
      return result;
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      console.error('Error fetching metrics:', error);
      return null;
    }
  }
  const handleAddStock = (stock: Stock) => {
    if (selectedStocks.length < 4 && !selectedStocks.find(s => s.symbol === stock.symbol)) {
      getMetricsBySymbol(stock.symbol).then(metrics => {
        if (metrics) {
          setSelectedStocks(prev => [...prev, { ...stock, metrics }]);
          const metricsList = Object.keys(metrics)
            .filter(key => metrics[key] !== null && metrics[key] !== undefined)
            .map(key => ({
              id: key,
              label: metrics[key].label || key.replace(/_/g, ' ').toUpperCase(),
            }));
          setTableMetrics(metricsList);
        } else {
          console.warn('No metrics found for stock:', stock.symbol);
        }
      });
    }
  };
  const handleRemoveStock = (symbol: string) => {
    setSelectedStocks(prev => prev.filter(stock => stock.symbol !== symbol));
  };
  const handleRemoveAllStocks = () => {
    setSelectedStocks([]);
    setTableMetrics([]);
  };

  const stocksToCompare = includeBenchmark ? [...selectedStocks, ...benchmarkMetrics] : selectedStocks;

  const Header = (
    <div
      className={
        variant === 'single'
          ? 'flex items-center justify-between px-3 py-1.5 bg-white/[0.04] border-b border-white/10'
          : 'flex items-center justify-between px-3 py-1.5 bg-white/[0.04] border border-white/10 rounded-lg mb-2'
      }
    >
      <h2 className="text-[11px] font-semibold text-theme-primary">Stock Comparison</h2>
      <div className='flex items-center gap-2'>
        <button
          type="button"
          onClick={handleToggleBenchmark}
          className={`text-[10px] px-2 py-1 rounded border transition-colors ${
            includeBenchmark ? 'bg-blue-500/90 text-theme-primary border-blue-500' : 'border-white/15 text-theme-secondary hover:bg-white/5'
          }`}
        >
          Compare to S&P 500
        </button>
        <button
          type="button"
          onClick={() => setIsComparisonCollapsed(!isComparisonCollapsed)}
          className="text-theme-primary/60 hover:text-theme-primary/80"
          aria-expanded={!isComparisonCollapsed}
        >
          {isComparisonCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
        </button>
      </div>
    </div>
  );

  const Body = (
    !isComparisonCollapsed && (
      <div className={variant === 'single' ? 'p-3' : 'rounded-lg border border-white/10 bg-white/5 overflow-hidden'}>
        <div className={variant === 'single' ? 'mx-auto w-full' : 'p-3'}>
          {/* Search Section */}
          <div className="mb-3">
            <StockSearchBar
              defaultValue={defaultStock ?? undefined}
              onSelectStock={handleAddStock}
              selectedStocks={selectedStocks}
              onRemoveStock={handleRemoveStock}
              maxStocks={3}
              onRemovedAllStocks={handleRemoveAllStocks}
            />
          </div>
          {/* Content Section */}
          <div className={variant === 'single' ? 'px-2' : 'px-2'}>
            {selectedStocks.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-theme-muted mb-2">Search and select stocks to compare</p>
                <p className="text-xs text-theme-muted">You can compare up to 3 stocks at once</p>
              </div>
            ) : (
              <div className="overflow-auto">
                {activeTab === 'table' && <TableView stocks={stocksToCompare} tableMetrics={tableMetrics} />}
                {activeTab === 'chart' && <ChartView stocks={stocksToCompare} />}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  );

  if (variant === 'single') {
    return (
      <div className="rounded-xl border border-white/10 bg-white/5 mx-2 my-2 overflow-hidden">
        {Header}
        {Body}
      </div>
    );
  }
  // flat variant
  return (
    <div className="mx-2 my-2">
      {Header}
      {Body}
    </div>
  );
}
export default StockComparison;